import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import timeZone from 'moment-timezone';
import 'moment/locale/zh-cn';
import moment from 'moment/moment.js';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Col, Collapse, Form, OverlayTrigger, Popover, Row, Tooltip } from 'react-bootstrap';
import OrderWorkflowActionButton from './OrderWorkflowActionButton';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import SubtleBadge from '../../components/common/SubtleBadge';
import { usePayOrdersMutation } from '../../slices/ordersApiSlice';
import { socket } from '../../socket';
import { useOrderCollaborationLock } from '../dashboard/processing-order/modal/common/OrderCollaborationLockContext';
import { useOrderDetailViewOnly } from '../dashboard/processing-order/modal/common/OrderDetailViewOnlyContext';
import { useOrderContext } from '../../providers/OrderProvider';
import { useStatisticsContext } from '../../providers/StatisticsProvider';
import formatCurrency from '../../utils/formatCurrency';
import { markLocalOrderMutation } from '../../utils/order/orderLocalMutationGuard';
import { canReceiveOrderPayment } from '../../utils/order/orderFulfillmentActions';

/** 待办主区预付款：默认可见的付款计划节点数（不含预付款本身） */
const PRE_PAYMENT_FOCUS_VISIBLE_SCHEDULE_COUNT = 2;

const normalizeIsPaid = (isPaid) => {
	if (typeof isPaid === 'boolean') return isPaid;
	if (typeof isPaid === 'string') return isPaid === 'true' || isPaid === 'True' || isPaid === 'TRUE';
	return false;
};

const sameScheduleNodeId = (left, right) => {
	if (left == null || right == null) return false;
	return String(left) === String(right);
};

const formatCreditAmountDraft = (raw) => {
	const cleaned = String(raw ?? '').replace(/[^\d.]/g, '');
	if (!cleaned) return '';
	const dot = cleaned.indexOf('.');
	const intRaw = dot === -1 ? cleaned : cleaned.slice(0, dot);
	const decimals = dot === -1 ? null : cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
	const intDigits = intRaw.replace(/^0+(?=\d)/, '');
	const grouped = (intDigits || (decimals != null ? '0' : '')).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	if (!grouped) return '';
	if (decimals == null) return `¥${grouped}`;
	return `¥${grouped}.${decimals}`;
};

const parseCreditAmountDraft = (display) => {
	const amount = Number(String(display ?? '').replace(/[¥,\s]/g, ''));
	if (!Number.isFinite(amount)) return 0;
	return Math.round(amount * 100) / 100;
};

const capCreditAmountDraft = (raw, unpaid) => {
	const draft = formatCreditAmountDraft(raw);
	const amount = parseCreditAmountDraft(draft);
	const cap = Math.round((Number(unpaid) || 0) * 100) / 100;
	if (!(amount > cap)) return draft;
	return cap > 0 ? formatCurrency(cap) : '';
};

const OrderPaymentScheduleNode = ({ isLast, modelName, pendingStepFocus }) => {
	const prePaymentCompact = pendingStepFocus === 'prePayment';
	const { userInfo } = useSelector((state) => state.auth);
	const [pendingPaymentKey, setPendingPaymentKey] = useState(null);
	/** 收款成功后立刻视为已付，直到详情缓存跟上（防 toast 成功但按钮仍在） */
	const [optimisticPaidKeys, setOptimisticPaidKeys] = useState([]);
	const paymentLockRef = useRef(false);
	const releasePaymentLock = useCallback(() => {
		paymentLockRef.current = false;
	}, []);
	const clearPendingPayment = useCallback(() => {
		releasePaymentLock();
		setPendingPaymentKey(null);
	}, [releasePaymentLock]);
	const [showMorePaymentNodes, setShowMorePaymentNodes] = useState(false);
	const [creditPayAmount, setCreditPayAmount] = useState('');
	const [payOrder, { isLoading: loadingPay }] = usePayOrdersMutation();
	const paymentBusy = Boolean(pendingPaymentKey) || loadingPay;
	const { refetchAROrders, refetchAcceptingOrders, refetchPrePaymentOrders, order, isOrderReady } = useOrderContext();
	const collaborationLocked = useOrderCollaborationLock();
	const viewOnly = useOrderDetailViewOnly();
	const { _id, orderId, scheduleNodes, accountsReceivables, isApproved, prePaymentPercent, percentPayment, approvalStatus, paymentStatus, isDeliveryPaid, isAcceptPaid } = order;
	const isApprovalRejected = approvalStatus === 'reject';
	const creditTermStarted = approvalStatus === 'approved' || approvalStatus === 'systemApproved';
	const actionDisabled = !isOrderReady || collaborationLocked || isApprovalRejected;
	const { refreshStatisticsAfterOrderChange } = useStatisticsContext();

	// 获取预付款信息
	const prePaymentAR = accountsReceivables?.find((a) => a.title === '预付') || null;
	const creditRepaymentLines = (accountsReceivables || []).filter((a) => a.title === '额度还款');
	const creditUnpaidTotal = creditRepaymentLines.reduce((sum, ar) => {
		if (normalizeIsPaid(ar.isPaid)) return sum;
		if (ar.creditUnpaid != null) return sum + (Number(ar.creditUnpaid) || 0);
		return sum + (Number(ar.accountsReceivable) || 0);
	}, 0);
	const creditRepaymentUnpaid = creditUnpaidTotal > 0;
	const canReceivePayment = !viewOnly && canReceiveOrderPayment(userInfo);
	const canEnterCreditReceipt = creditRepaymentUnpaid && canReceivePayment && isApproved;
	const currentCreditPayLine = creditRepaymentLines
		.filter((ar) => {
			if (normalizeIsPaid(ar.isPaid)) return false;
			const unpaid = ar.creditUnpaid != null ? Number(ar.creditUnpaid) || 0 : Number(ar.accountsReceivable) || 0;
			return unpaid > 0;
		})
		.slice()
		.sort((a, b) => new Date(a.expire_time) - new Date(b.expire_time) || String(a._id).localeCompare(String(b._id)))[0];
	const currentCreditPayLineId = currentCreditPayLine?._id;
	const currentCreditPayUnpaid = currentCreditPayLine
		? currentCreditPayLine.creditUnpaid != null
			? Number(currentCreditPayLine.creditUnpaid) || 0
			: Number(currentCreditPayLine.accountsReceivable) || 0
		: 0;

	// 判断预付款金额是否为0
	const prePaymentAmount = prePaymentAR?.accountsReceivable || percentPayment || 0;
	const isPrePaymentZero = prePaymentAmount === 0;
	const paymentFlowCompleted = !creditRepaymentUnpaid && (paymentStatus === 'payComplete' || Boolean(isDeliveryPaid && isAcceptPaid));
	const hasRenderablePrePaymentStep = isPrePaymentZero || Boolean(prePaymentAR);
	const renderableScheduleNodes = useMemo(() => {
		if (!Array.isArray(scheduleNodes) || !Array.isArray(accountsReceivables)) return [];
		if (scheduleNodes.length === 0 || accountsReceivables.length === 0) return [];
		return scheduleNodes;
	}, [accountsReceivables, scheduleNodes]);
	const primaryScheduleNodes = prePaymentCompact ? renderableScheduleNodes.slice(0, PRE_PAYMENT_FOCUS_VISIBLE_SCHEDULE_COUNT) : renderableScheduleNodes;
	const collapsedScheduleNodes = prePaymentCompact ? renderableScheduleNodes.slice(PRE_PAYMENT_FOCUS_VISIBLE_SCHEDULE_COUNT) : [];
	const hasCollapsedPaymentNodes = prePaymentCompact && (collapsedScheduleNodes.length > 0 || renderableScheduleNodes.length > 0);

	const isNodePaid = useCallback(
		(ar, schedule, isPrePayment = false) => {
			if (normalizeIsPaid(ar?.isPaid)) return true;
			if (ar?.title === '额度还款') return false;
			if (isPrePayment) return optimisticPaidKeys.includes('prePayment');
			const key = String(ar?.scheduleNode ?? schedule?._id ?? '');
			return Boolean(key) && optimisticPaidKeys.includes(key);
		},
		[optimisticPaidKeys],
	);

	useEffect(() => {
		setOptimisticPaidKeys((prev) => {
			if (prev.length === 0) return prev;
			const next = prev.filter((key) => {
				if (key === 'prePayment') return !normalizeIsPaid(prePaymentAR?.isPaid);
				return !(accountsReceivables || []).some((item) => sameScheduleNodeId(item.scheduleNode, key) && normalizeIsPaid(item.isPaid));
			});
			return next.length === prev.length ? prev : next;
		});
	}, [accountsReceivables, prePaymentAR?.isPaid]);

	useEffect(() => {
		setCreditPayAmount(currentCreditPayUnpaid > 0 ? String(Math.round(currentCreditPayUnpaid * 100) / 100) : '');
	}, [currentCreditPayLineId, currentCreditPayUnpaid]);

	const latestPaidRecord = (accountsReceivables || [])
		.filter((item) => normalizeIsPaid(item?.isPaid) && item?.paidAt)
		.reduce((latest, item) => {
			if (!latest) return item;
			return new Date(item.paidAt).getTime() > new Date(latest.paidAt).getTime() ? item : latest;
		}, null);
	// 创建固定的预付款节点（无论金额是否为0都创建）
	const prePaymentNode = {
		_id: 'prePayment',
		icon: 'fa-sack-dollar',
		title: '预付款',
		paymentDays: 0,
		paymentPercent: prePaymentAR?.paymentPercent || prePaymentPercent || 0,
		paymentAmount: prePaymentAmount,
		isCompleted: isNodePaid(prePaymentAR, null, true),
		completedAt: prePaymentAR?.paidAt,
		completedResult: prePaymentAR?.paymentResult
			? {
					operator_name: prePaymentAR.paymentResult.operator,
					operator: prePaymentAR.paymentResult.operator,
				}
			: null,
	};

	const onPayOrder = useCallback(
		async (accountsReceivable, title, scheduleNode, orderOId, orderId) => {
			markLocalOrderMutation(orderOId);
			await payOrder({ orderOId, details: { payAccountsReceivable: accountsReceivable, title, scheduleNode } }).unwrap();
			markLocalOrderMutation(orderOId);

			if (modelName === 'AccountsReceivableModal') {
				refetchAROrders();
			}

			if (modelName === 'AcceptModal') {
				refetchAcceptingOrders();
			}

			if (modelName === 'PrePaymentModal') {
				refetchPrePaymentOrders?.();
			}
			refreshStatisticsAfterOrderChange();
			socket.emit('refresh processing orderlist');
			socket.emit('refresh dashboard stats');
			toast.success(orderId + '订单' + title + '款项已核销!');
		},
		[modelName, payOrder, refetchAROrders, refetchAcceptingOrders, refetchPrePaymentOrders, refreshStatisticsAfterOrderChange],
	);

	useEffect(() => {
		if (!pendingPaymentKey) return;
		if (pendingPaymentKey === 'prePayment') {
			if (normalizeIsPaid(prePaymentAR?.isPaid) || optimisticPaidKeys.includes('prePayment')) clearPendingPayment();
			return;
		}
		const paid =
			optimisticPaidKeys.includes(pendingPaymentKey) ||
			accountsReceivables?.some((item) => sameScheduleNodeId(item.scheduleNode, pendingPaymentKey) && normalizeIsPaid(item.isPaid));
		if (paid) clearPendingPayment();
	}, [pendingPaymentKey, prePaymentAR, accountsReceivables, clearPendingPayment, optimisticPaidKeys]);

	const handlePayClick = useCallback(
		async (nodeKey, accountsReceivable, title, scheduleNode) => {
			if (paymentLockRef.current || paymentBusy || !_id) return;
			if (optimisticPaidKeys.includes(nodeKey)) return;
			if (nodeKey === 'prePayment' && normalizeIsPaid(prePaymentAR?.isPaid)) return;
			if (
				nodeKey !== 'prePayment' &&
				(accountsReceivables || []).some((item) => sameScheduleNodeId(item.scheduleNode, nodeKey) && normalizeIsPaid(item.isPaid))
			) {
				return;
			}

			paymentLockRef.current = true;
			setPendingPaymentKey(nodeKey);
			/** 先乐观锁死本节点，避免连点发出第二次请求 */
			setOptimisticPaidKeys((prev) => (prev.includes(nodeKey) ? prev : [...prev, nodeKey]));
			try {
				await onPayOrder(accountsReceivable, title, scheduleNode, _id, orderId);
				clearPendingPayment();
			} catch (err) {
				const status = err?.status || err?.originalStatus || err?.data?.statusCode;
				const alreadyPaid = status === 409 || /已核销|重复/.test(String(err?.data?.message || err?.message || ''));
				if (alreadyPaid) {
					/** 服务端判定已付：保持乐观态，并尽量把缓存同步到最新 */
					clearPendingPayment();
					toast.info(err?.data?.message || '该应收已核销');
					return;
				}
				setOptimisticPaidKeys((prev) => prev.filter((key) => key !== nodeKey));
				clearPendingPayment();
				toast.error(err?.data?.message || err?.message || '收款确认失败，请稍后重试');
			}
		},
		[paymentBusy, _id, orderId, onPayOrder, clearPendingPayment, optimisticPaidKeys, prePaymentAR?.isPaid, accountsReceivables],
	);

	const getScheduleAccountsReceivable = (schedule) =>
		accountsReceivables.find((a) => sameScheduleNodeId(a.scheduleNode, schedule._id)) || {};

	// 渲染单个付款节点
	const renderPaymentNode = (schedule, ar, index, isPrePayment = false, nodeIsLast = isLast) => {
		const isCreditRepayment = ar?.title === '额度还款';
		const creditReleased = Number(ar?.creditReleased) || 0;
		const creditUnpaid = isCreditRepayment
			? ar?.creditUnpaid != null
				? Number(ar.creditUnpaid) || 0
				: Math.max(0, (Number(ar?.accountsReceivable) || 0) - creditReleased)
			: 0;
		const nodeKey = isCreditRepayment ? String(ar?._id || index) : isPrePayment ? 'prePayment' : String(ar?.scheduleNode ?? schedule?._id ?? index);
		const isNodePending = pendingPaymentKey === nodeKey;
		const paid = isNodePaid(ar, schedule, isPrePayment);
		const calculatedDays = ar?.expire_time ? timeZone.tz(moment(ar.expire_time), 'Asia/Shanghai').diff(timeZone.tz(moment(), 'Asia/Shanghai'), 'days') : 0;
		const paymentSettleDayDiff =
			!isPrePayment && normalizeIsPaid(ar?.isPaid) && ar?.paidAt && ar?.expire_time
				? timeZone.tz(moment(ar.paidAt), 'Asia/Shanghai').startOf('day').diff(timeZone.tz(moment(ar.expire_time), 'Asia/Shanghai').startOf('day'), 'days')
				: null;
		let prePaymentCollectAfterDays = null;
		if (isPrePayment && ar?.start_time && normalizeIsPaid(ar.isPaid) && ar.paidAt) {
			prePaymentCollectAfterDays = timeZone
				.tz(moment(ar.paidAt), 'Asia/Shanghai')
				.startOf('day')
				.diff(timeZone.tz(moment(ar.start_time), 'Asia/Shanghai').startOf('day'), 'days');
		}
		return (
			<Row
				className={classNames('g-3 recent-activity-timeline recent-activity-timeline-primary', {
					'pb-x1': true,
					'recent-activity-timeline-past': schedule.isCompleted,
					'recent-activity-timeline-current': ar && !paid,
				})}
				key={isCreditRepayment ? `credit-${ar?._id || index}` : isPrePayment ? 'prePayment' : index}
			>
				<Col xs='auto' className='ps-4 ms-2'>
					<div className='ps-2'>
						<div className='icon-item icon-item-sm rounded-circle bg-200 shadow-none'>
							<FontAwesomeIcon icon={schedule.icon} className='text-primary' />
						</div>
					</div>
				</Col>
				<Col className={classNames({ 'border-bottom pb-x1': !nodeIsLast && !(isPrePayment && prePaymentCompact && !hasCollapsedPaymentNodes) })}>
					<div className='d-flex justify-content-between align-items-start gap-2 mb-2'>
						<h6 className='mb-0'>
							{schedule.title}
							<SubtleBadge className='ms-2'>{schedule.paymentPercent}%</SubtleBadge>
							{(isPrePayment ? paid : schedule.isCompleted) &&
								(paid ? (
									<Badge className='ms-2' bg='success'>
										已收款
									</Badge>
								) : (
									(isPrePayment || isApproved) && (
										<Badge className='ms-2' bg='warning'>
											待确认款项
										</Badge>
									)
								))}
						</h6>
						{isCreditRepayment && canEnterCreditReceipt && String(ar?._id) === String(currentCreditPayLineId) ? (
							pendingPaymentKey === 'creditRepayment' ? (
								<OrderWorkflowActionButton pending pendingLabel='处理中…' icon='fa-solid fa-stamp'>
									收款
								</OrderWorkflowActionButton>
							) : (
								<OverlayTrigger
									trigger='click'
									placement='bottom-end'
									rootClose
									container={typeof document !== 'undefined' ? document.body : undefined}
									overlay={
										<Popover id={`credit-receipt-entry-${ar?._id || index}`}>
											<Popover.Header as='div' className='py-2 px-3 fs-10 fw-semibold'>
												收款
											</Popover.Header>
											<Popover.Body className='py-2 px-3'>
												<Form.Control
													size='sm'
													className='fs-11 mb-2'
													inputMode='decimal'
													placeholder='请填写金额'
													value={creditPayAmount}
													autoFocus
													disabled={actionDisabled || paymentBusy}
													onChange={(event) => setCreditPayAmount(capCreditAmountDraft(event.target.value, creditUnpaidTotal))}
													onBlur={() => {
														const amount = Math.min(parseCreditAmountDraft(creditPayAmount), Math.round((Number(creditUnpaidTotal) || 0) * 100) / 100);
														if (amount > 0) setCreditPayAmount(formatCurrency(amount));
													}}
												/>
												<div className='fs-10 text-600 mb-2'>未付 {formatCurrency(creditUnpaidTotal)}</div>
												<Button
													size='sm'
													variant='outline-warning'
													className='fs-11 w-100'
													disabled={actionDisabled || paymentBusy}
													onClick={() => {
														const amount = parseCreditAmountDraft(creditPayAmount);
														if (!(amount > 0)) {
															toast.error('请填写收款金额');
															return;
														}
														if (Math.round(amount * 100) > Math.round(creditUnpaidTotal * 100)) {
															toast.error('收款金额超过未付额度还款');
															return;
														}
														if (paymentLockRef.current || !_id) return;
														paymentLockRef.current = true;
														setPendingPaymentKey('creditRepayment');
														onPayOrder(amount, '额度还款', null, _id, orderId)
															.then(() => {
																setCreditPayAmount('');
																clearPendingPayment();
															})
															.catch((err) => {
																clearPendingPayment();
																toast.error(err?.data?.message || err?.error || '收款失败');
															});
													}}
												>
													确认
												</Button>
											</Popover.Body>
										</Popover>
									}
								>
									<Button
										size='sm'
										className='fs-11'
										variant='outline-warning'
										disabled={actionDisabled || paymentBusy || approvalStatus === 'inProcessOfApproval'}
									>
										<FontAwesomeIcon icon='fa-solid fa-stamp' className='me-1' />
										收款
									</Button>
								</OverlayTrigger>
							)
						) : null}
						{!isApprovalRejected &&
							canReceivePayment &&
							ar &&
							!paid &&
							// 预付款：需要订单已审批且金额不为0；其他节点：需要节点已完成
							((isPrePayment && !isCreditRepayment && isApproved && prePaymentAmount > 0) || (!isPrePayment && !isCreditRepayment && schedule.isCompleted)) && (
								<OrderWorkflowActionButton
									pending={isNodePending}
									icon='fa-solid fa-stamp'
									disabled={actionDisabled || paymentBusy || paid || approvalStatus === 'inProcessOfApproval'}
									onClick={() => {
										if (paid || paymentBusy || paymentLockRef.current) return;
										if (isPrePayment || isCreditRepayment) {
											handlePayClick(nodeKey, ar.accountsReceivable, ar.title, null);
										} else {
											const unpaid = accountsReceivables.find((node) => sameScheduleNodeId(node.scheduleNode, schedule._id) && !normalizeIsPaid(node.isPaid));
											if (!unpaid) return;
											handlePayClick(nodeKey, unpaid.accountsReceivable, unpaid.title, unpaid.scheduleNode);
										}
									}}
								>
									收款
								</OrderWorkflowActionButton>
							)}
						{paid && (
							<div className='text-end flex-shrink-0 text-nowrap'>
								{ar?.paidAt ? (
									<p className='fs-11 text-600 mb-0'>{timeZone.tz(moment(ar.paidAt), 'Asia/Shanghai').format('YYYY-MM-DD HH:mm')}</p>
								) : (
									<p className='fs-11 text-600 mb-0'>刚刚确认</p>
								)}
								<p className='fs-11 text-600 mb-0'>
									<OverlayTrigger
										key='top'
										placement='top'
										overlay={
											<Tooltip style={{ position: 'fixed' }} id={'tooltip-top'} className='fs-11'>
												当前节点由<span className='mx-1'>{ar?.paymentResult?.operator || userInfo?.name || '你'}</span>确认收款
											</Tooltip>
										}
									>
										<FontAwesomeIcon icon='user-circle' className='text-primary me-1 fs-11' />
									</OverlayTrigger>
									<span className='text-primary-emphasis fs-11 fw-semibold'>{ar?.paymentResult?.operator || userInfo?.name || '你'}确认收款</span>
								</p>
							</div>
						)}
					</div>
					<ul className='fs-10 text-600 mb-0 list-unstyled text-nowrap'>
						{!isPrePayment && (
							<li>
								<span className='me-3'>
									<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
									{ar?.start_time ? (
										<>
											开始日期: {timeZone.tz(moment(ar.start_time), 'Asia/Shanghai').format('YYYY-MM-DD')}
											<span className='mx-1'>·</span>
										</>
									) : null}
									账期: {schedule.paymentDays}天
								</span>
							</li>
						)}
						{isPrePayment && ar?.start_time && (
							<li>
								<span className='me-3'>
									<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
									开始日期: {timeZone.tz(moment(ar.start_time), 'Asia/Shanghai').format('YYYY-MM-DD')}
									{prePaymentCollectAfterDays != null ? (
										<>
											<span className='mx-1'>·</span>
											{prePaymentCollectAfterDays > 0
												? `${prePaymentCollectAfterDays} 天后收款`
												: '当天收款'}
										</>
									) : null}
								</span>
							</li>
						)}
						<li>
							<span className='me-3'>
								<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
								{isCreditRepayment ? (
									<>
										计划 {formatCurrency(ar?.accountsReceivable)}
										<span className='mx-1'>·</span>
										{(ar?.creditReceipts || []).length > 0 ? (
											<OverlayTrigger
												trigger={['hover', 'focus']}
												placement='top'
												container={typeof document !== 'undefined' ? document.body : undefined}
												overlay={
													<Popover id={`credit-receipts-${ar?._id || index}`}>
														<Popover.Header as='div' className='py-2 px-3 fs-10 fw-semibold'>
															收款流水
														</Popover.Header>
														<Popover.Body className='py-2 px-3 fs-10'>
															<ul className='list-unstyled mb-0'>
																{(ar.creditReceipts || []).map((receipt) => (
																	<li key={receipt._id} className='py-1'>
																		<div>{receipt.createdAt ? timeZone.tz(moment(receipt.createdAt), 'Asia/Shanghai').format('YYYY-MM-DD HH:mm') : ''}</div>
																		<div>
																			收款 {formatCurrency(receipt.amount)}
																			{receipt.operator ? ` · ${receipt.operator}` : ''}
																		</div>
																	</li>
																))}
															</ul>
														</Popover.Body>
													</Popover>
												}
											>
												<span className='text-primary' style={{ cursor: 'pointer', borderBottom: '1px dashed currentColor' }}>
													已收 {formatCurrency(creditReleased)}
												</span>
											</OverlayTrigger>
										) : (
											<>已收 {formatCurrency(creditReleased)}</>
										)}
										<span className='mx-1'>·</span>
										未付 {formatCurrency(creditUnpaid)}
									</>
								) : (
									<>金额: {formatCurrency(schedule.paymentAmount)}</>
								)}
							</span>
						</li>
						{isCreditRepayment && !paid && !creditTermStarted && creditUnpaid > 0 && (
							<li>
								<span className='fw-semibold text-600'>审批通过后开始计算账期</span>
							</li>
						)}
						{ar && !paid && ar?.expire_time && (isCreditRepayment ? creditTermStarted && creditUnpaid > 0 : !isPrePayment || schedule.isCompleted) && (
							<>
								<li>
									<span className={classNames('fw-semibold', calculatedDays < 7 ? 'text-danger' : 'text-warning')}>
										<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
										{timeZone.tz(moment(ar?.expire_time), 'Asia/Shanghai').format('YYYY-MM-DD')}到期
									</span>
								</li>
								<li>
									<span className={classNames('fw-semibold', calculatedDays < 7 ? 'text-danger' : 'text-warning')}>
										<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
										{calculatedDays < 0 ? (
											<span>
												超期 {Math.abs(calculatedDays)} 天 <FontAwesomeIcon icon='fa-calendar' beat className='text-danger' />
											</span>
										) : calculatedDays === 0 ? (
											<span>
												今日到期，请及时催收款项!
												<FontAwesomeIcon icon='fa-triangle-exclamation' beat className='text-danger ' />
											</span>
										) : (
											`剩余 ${calculatedDays} 天`
										)}
									</span>
								</li>
							</>
						)}
						{paymentSettleDayDiff != null && ar?.expire_time && (
							<li>
								<span
									className={classNames('me-3 fw-semibold', {
										'text-warning': paymentSettleDayDiff > 0,
										'text-success': paymentSettleDayDiff <= 0,
									})}
								>
									<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
									{timeZone.tz(moment(ar.expire_time), 'Asia/Shanghai').format('YYYY-MM-DD')}到期
									<span className='mx-1'>·</span>
									{paymentSettleDayDiff > 0
										? `超期 ${paymentSettleDayDiff} 天确认`
										: paymentSettleDayDiff < 0
											? `提前 ${Math.abs(paymentSettleDayDiff)} 天确认`
											: '当天确认'}
								</span>
							</li>
						)}
					</ul>
				</Col>
			</Row>
		);
	};

	const renderPaymentCompleteRow = (rowIsLast = isLast) => (
		<Row
			className={classNames('g-3 recent-activity-timeline recent-activity-timeline-primary', {
				'recent-activity-timeline-past': paymentFlowCompleted,
				'recent-activity-timeline-current': !paymentFlowCompleted,
			})}
		>
			<Col xs='auto' className='ps-4 ms-2'>
				<div className='ps-2'>
					<div className='icon-item icon-item-sm rounded-circle bg-200 shadow-none'>
						<FontAwesomeIcon icon='fa-thumbs-up' className='text-primary' />
					</div>
				</div>
			</Col>
			<Col>
				<Row className={classNames('g-3', { 'border-bottom pb-x1': !rowIsLast })}>
					<Col>
						<div className='mb-2'>
							<h6>支付完成</h6>
						</div>
						{!paymentFlowCompleted ? <p className='fs-10 text-600 mb-0'>待支付完成</p> : null}
					</Col>
					<Col xs='auto'>
						{paymentFlowCompleted ? (
							<>
								{latestPaidRecord?.paidAt ? (
									<p className='fs-11 text-600 mb-0'>{timeZone.tz(moment(latestPaidRecord.paidAt), 'Asia/Shanghai').format('YYYY-MM-DD HH:mm')}</p>
								) : null}
							</>
						) : null}
					</Col>
				</Row>
			</Col>
		</Row>
	);

	const renderScheduleNodeList = (nodes, { keyPrefix = 'schedule', lastNodeIsLast = isLast } = {}) =>
		nodes.map((schedule, index) => {
			const ar = getScheduleAccountsReceivable(schedule);
			const nodeIsLast = lastNodeIsLast && index === nodes.length - 1;
			return renderPaymentNode(schedule, ar, `${keyPrefix}-${index}`, false, nodeIsLast);
		});

	return (
		<div className='position-relative px-0 pt-2 pb-1'>
			<div className='px-1'>
				{/* 固定的预付款节点 */}
				{hasRenderablePrePaymentStep ? (
					isPrePaymentZero ? (
						// 预付款为0时显示提示信息
						<Row
							className={classNames('g-3 recent-activity-timeline recent-activity-timeline-primary mt-2', {
								'pb-x1': primaryScheduleNodes.length > 0 || hasCollapsedPaymentNodes || creditRepaymentLines.length > 0,
							})}
						>
							<Col xs='auto' className='ps-4 ms-2'>
								<div className='ps-2'>
									<div className='icon-item icon-item-sm rounded-circle bg-200 shadow-none'>
										<FontAwesomeIcon icon='fa-sack-dollar' className='text-primary' />
									</div>
								</div>
							</Col>
							<Col>
								<Row
									className={classNames('g-3', {
										'border-bottom pb-x1': primaryScheduleNodes.length > 0 || hasCollapsedPaymentNodes || creditRepaymentLines.length > 0,
									})}
								>
									<Col>
										<div className='mb-2'>
											<h6>预付金额</h6>
										</div>
										<p className='fs-11 text-600 mb-0'>
											<span className='text-success-emphasis fw-semibold'>
												<FontAwesomeIcon icon='check' className='text-success me-2' />
												订单无需支付预付款
											</span>
										</p>
									</Col>
								</Row>
							</Col>
						</Row>
					) : (
						// 预付款不为0时显示完整的付款节点
						prePaymentAR && renderPaymentNode(prePaymentNode, prePaymentAR, -1, true, !hasCollapsedPaymentNodes && primaryScheduleNodes.length === 0 && isLast)
					)
				) : null}
				{creditRepaymentLines.map((creditRepaymentAR, creditIndex) =>
					renderPaymentNode(
						{
							_id: creditRepaymentAR._id,
							icon: 'fa-sack-dollar',
							title: '额度还款',
							paymentDays: 0,
							paymentPercent: creditRepaymentAR.paymentPercent || 100,
							paymentAmount: creditRepaymentAR.accountsReceivable,
							isCompleted: normalizeIsPaid(creditRepaymentAR.isPaid),
							completedAt: creditRepaymentAR.paidAt,
							completedResult: creditRepaymentAR.paymentResult
								? { operator_name: creditRepaymentAR.paymentResult.operator, operator: creditRepaymentAR.paymentResult.operator }
								: null,
						},
						creditRepaymentAR,
						`credit-${creditIndex}`,
						true,
						creditIndex === creditRepaymentLines.length - 1 && !creditRepaymentUnpaid && primaryScheduleNodes.length === 0 && isLast,
					),
				)}
				{/* 其他付款节点 */}
				{prePaymentCompact
					? renderScheduleNodeList(primaryScheduleNodes, {
							keyPrefix: 'primary',
							lastNodeIsLast: !hasCollapsedPaymentNodes && isLast,
						})
					: renderScheduleNodeList(renderableScheduleNodes)}
				{hasCollapsedPaymentNodes ? (
					<>
						<Button
							variant='link'
							size='sm'
							className='d-inline-flex align-items-center gap-1 px-0 mt-1 mb-0 fs-10 text-decoration-none'
							onClick={() => setShowMorePaymentNodes((v) => !v)}
							aria-expanded={showMorePaymentNodes}
						>
							<FontAwesomeIcon icon={showMorePaymentNodes ? 'chevron-up' : 'chevron-down'} className='fa-fw' />
							{showMorePaymentNodes ? '收起其余支付节点' : '查看其余支付节点'}
						</Button>
						<Collapse in={showMorePaymentNodes}>
							<div className='pt-1'>
								{renderScheduleNodeList(collapsedScheduleNodes, {
									keyPrefix: 'collapsed',
									lastNodeIsLast: false,
								})}
								{renderPaymentCompleteRow(isLast)}
							</div>
						</Collapse>
					</>
				) : !prePaymentCompact ? (
					renderPaymentCompleteRow(isLast)
				) : null}
			</div>
		</div>
	);
};

OrderPaymentScheduleNode.propTypes = {
	isLast: PropTypes.bool,
	modelName: PropTypes.string,
	pendingStepFocus: PropTypes.oneOf(['prePayment']),
};

export default OrderPaymentScheduleNode;
